import React, { useContext } from 'react'
import { MuiPickersContext } from '@material-ui/pickers'
import merge from 'lodash/merge'
import set from 'lodash/set'
import { useAbility } from 'currentContext'
import { useField } from 'formik'
import EntityContext, { EntityContextType } from 'components/item/context'
import MuiInputLabel from '@material-ui/core/InputLabel'
import { makeStyles, withStyles } from '@material-ui/core/styles'

const useStyles = makeStyles((theme) => ({
  valueStyle: {
    padding: theme.spacing(2, 0, 1, 0),
    color: theme.palette.grey[900],
    fontWeight: 600,
    fontSize: '0.929rem'
  }
}))

const InputLabel = withStyles(() => ({
  root: {
    fontWeight: 600
  }
}))(MuiInputLabel)

export interface EntityContextInjectedProps {
  abilityField?: string
}

export default function withEntityContext<A extends object, V = unknown, P extends { [x: string]: any } = {}>(
  Component: React.ComponentType<P>,
  getTextValue: (value: V | V[], props?: P & A, pickersContext?: any) => React.ReactNode,
  path?: string
): React.ComponentType<P & A & EntityContextInjectedProps> {
  // eslint-disable-next-line react/display-name
  return ({ abilityField, ...props }: React.PropsWithChildren<P & A & EntityContextInjectedProps>) => {
    const context = useContext(EntityContext) as EntityContextType | null
    const ability = useAbility()
    const pickersContext = useContext(MuiPickersContext)
    const componentProps = { ...props }
    const classes = useStyles()

    const mergeDisabledProp = (object: object, disabled: boolean): void => {
      if (!disabled) {
        return
      }

      merge(object, set({}, path !== undefined ? `${path}.disabled` : 'disabled', true))
    }

    let value: V | V[]

    if (props.name === undefined) {
      value = undefined as unknown as V
    } else if (Array.isArray(props.name)) {
      value = []

      props.name.forEach((name: V[]) => {
        ;(value as V[]).push(useField(name)[0].value)
      })
    } else {
      ;[{ value }] = useField(props.name)
    }

    if (context !== null) {
      const canUpdate = (fieldName: string | string[]): boolean => {
        if (Array.isArray(componentProps.name)) {
          componentProps.name.forEach((name: string) => {
            return ability.can('update', context.authorizationSubject, name)
          })
        } else {
          return ability.can('update', context.authorizationSubject, fieldName as string)
        }

        return false
      }

      if (componentProps.name !== undefined) {
        mergeDisabledProp(componentProps, !canUpdate(abilityField ?? componentProps.name))
      } else if (props.items !== undefined) {
        componentProps.items.forEach((item: { name: string }) => mergeDisabledProp(item, !canUpdate(item.name)))
      }
    }

    return (
      <>
        {context === null || context.editing ? (
          <Component {...(componentProps as P & A)} />
        ) : (
          <>
            <InputLabel shrink>
              {componentProps.label ?? componentProps.TextFieldProps?.label ?? componentProps.KeyboardDatePickerProps?.label}
            </InputLabel>
            <div className={classes.valueStyle}>{getTextValue(value, props as P & A, pickersContext)}</div>
          </>
        )}
      </>
    )
  }
}
